/* This file is auto-generated by build_string_refs.ts, do not modify directly */

import type { I18nKey } from '../../../i18n-key.js'

/**
 * - content: `Pick an account to request access`
 * - description: Guidance for users to select an account when viewing the account picker modal
 */
export const authAccountPicker = "auth.account-picker" as const as I18nKey
/**
 * - content: `Choose an account to join {destination}`
 * - description: Guidance for users to select which account they are using to join a team
 */
export const authAccountPickerJoinText = "auth.account-picker-join-text" as const as I18nKey<{ "destination": string | number | boolean }>
/**
 * - content: `Choose an account to open {destination}`
 * - description: Guidance for users to select which account they are using to open a file
 */
export const authAccountPickerOpenText = "auth.account-picker-open-text" as const as I18nKey<{ "destination": string | number | boolean }>
/**
 * - content: `If you don't see the account you want to use, you can {addAnotherLink}.`
 * - description: User cannot log in with one of their currently-authenticated accounts and needs to log into (or create) another. {addAnotherLink} is a button that says 'add another one' in English, and has the key 'auth.add-another-one'
 */
export const authAddAnotherAccountWithLink = "auth.add-another-account-with-link" as const as I18nKey<{ "addAnotherLink": string | number | boolean }>
/**
 * - content: `add another one`
 * - description: Call to action on the account switcher modal to allow a user to add another account to switch to. Does not include punctuation as it is a component of the string with key 'auth.add-another-account-with-link'
 */
export const authAddAnotherNoPunctuation = "auth.add-another-no-punctuation" as const as I18nKey
/**
 * - content: `Captcha is loading...`
 * - description: This string appears while the browser is loading the captcha during the signup process.
 */
export const authCaptchaCaptchaIsLoading = "auth.captcha.captcha_is_loading" as const as I18nKey
/**
 * - content: `Reload Captcha`
 * - description: This string appears on the button to reload the captcha puzzle if it hasn't loaded in the allocated time.
 */
export const authCaptchaReloadCaptcha = "auth.captcha.reload_captcha" as const as I18nKey
/**
 * - content: `If you're having trouble loading the Captcha puzzle, please refer to our {helpCenterLink}.`
 * - description: This string appears when captcha doesn't load after the allocated time. It points users to a Help Center link to troubleshoot their issue.
 */
export const authCaptchaTimedOutWhileLoading = "auth.captcha.timed_out_while_loading" as const as I18nKey<{ "helpCenterLink": string | number | boolean }>
/**
 * - content: `Help Center`
 * - description: Link to a Help Center article that helps users troubleshoot issues with loading the captcha puzzle.
 */
export const authCaptchaTimedOutWhileLoadingHelpCenterLinkText = "auth.captcha.timed_out_while_loading.help_center_link_text" as const as I18nKey
/**
 * - content: `Choose account to add to Figma for VS Code`
 * - description: Instructions for user to choose which account they're logging into for Figma for VS Code
 */
export const authChooseAccountToAddToFigmaForVsCode = "auth.choose-account-to-add-to-figma-for-vs-code" as const as I18nKey
/**
 * - content: `Choose account to add to the Figma {appDescriptor} app`
 * - description: Instructions for user to choose which account they're logging into
 */
export const authChooseAccountToAddToSpecificApp = "auth.choose-account-to-add-to-specific-app" as const as I18nKey<{ "appDescriptor": string | number | boolean }>
/**
 * - content: `Log in or create an account to collaborate`
 * - description: Header on a form where users log in or sign up to access a generic resource they were invited to
 */
export const authClaimInviteHeader = "auth.claim-invite.header" as const as I18nKey
/**
 * - content: `Log in or create an account to collaborate on this file`
 * - description: Header on a form where users log in or sign up to access a file they were invited to
 */
export const authClaimInviteHeaderFile = "auth.claim-invite.header-file" as const as I18nKey
/**
 * - content: `Continue`
 * - description: Button text for starting the continue with Google SSO process
 */
export const authContinue = "auth.continue" as const as I18nKey
/**
 * - content: `Continue with {appDescriptor} app`
 * - description: Instructs user to try authenticating again in one of three apps: mobile, desktop, or mirror
 */
export const authContinueWithTheSpecificApp = "auth.continue-with-the-specific-app" as const as I18nKey<{ "appDescriptor": string | number | boolean }>
/**
 * - content: `Cookies are disabled`
 * - description: Title on an auth page telling user that they need to enable browser cookies to be able to log in and use figma
 */
export const authCookiesAreDisabled = "auth.cookies_are_disabled" as const as I18nKey
/**
 * - content: `Cookies are required to log in and use Figma. Please enable cookies in your browser settings and refresh the page.`
 * - description: Subtitle on an auth page telling user that they need to enable browser cookies to be able to log in and use figma
 */
export const authCookiesAreRequiredToUseFigma = "auth.cookies_are_required_to_use_figma" as const as I18nKey
/**
 * - content: `Create one`
 * - description: For users that do not have accounts, a call-to-action that lets them make a new one
 */
export const authCreateOne = "auth.create-one" as const as I18nKey
/**
 * - content: `Create an account to view and reply to comments.`
 * - description: Prompt for users to create account to leave comments on designs
 */
export const authCreateAccountToViewReplyComments = "auth.create_account_to_view_reply_comments" as const as I18nKey
/**
 * - content: `Create an account to collaborate on {displayName}`
 * - description: This string appears in the header of the signup modal on logged-out Community pages
 */
export const authCreateAnAccountToCollaborateOnDisplayName = "auth.create_an_account_to_collaborate_on_display_name" as const as I18nKey<{ "displayName": string | number | boolean }>
/**
 * - content: `Create an account to collaborate on this file`
 * - description: This string appears in the header of the signup modal on logged-out Community pages
 */
export const authCreateAnAccountToCollaborateOnThisFile = "auth.create_an_account_to_collaborate_on_this_file" as const as I18nKey
/**
 * - content: `An error occurred`
 * - description: A general error message displayed if two factor authentication fails
 */
export const authDefaultError = "auth.default-error" as const as I18nKey
/**
 * - content: `desktop`
 * - description: Designator for our desktop app
 */
export const authDesktopApp = "auth.desktop-app" as const as I18nKey
/**
 * - content: `This shared devbox account is deprecated. Use your personalized account instead (yourname+devbox-'<plan>-<role>'@test.figma.com) so changes are isolated to your account.`
 * - description: Warning banner shown to Figma engineers on the sign-in form in development environments when they try to log in with a deprecated shared devbox test account, nudging them to use their employee-specific test account instead
 */
export const authDevboxDeprecatedAccountWarning = "auth.devbox-deprecated-account-warning" as const as I18nKey
/**
 * - content: `Do you want to get tips and updates from Figma?`
 * - description: This is a headline in a modal dialog asking users if they want to receive tips and updates from Figma emails The modal includes a newsletter icon, this question text, additional consent information below, and "Yes, please" and "No, thanks" buttons for the user to make their choice.
 */
export const authDoYouWantToGetTips = "auth.do_you_want_to_get_tips" as const as I18nKey
/**
 * - content: `Login | Figma`
 * - description: This string appears as the browser tab's title tag when the user is on our login page (figma.com/login). It should be consistent with the string of the same ID in the Sinatra dictionary.
 */
export const authDocumentTitleLoginFigma = "auth.document_title.login_figma" as const as I18nKey
/**
 * - content: `Sign Up for Figma`
 * - description: This string appears as the browser tab's title tag when the user is on our sign up page (figma.com/signup). It should be consistent with the string of the same ID in the Sinatra dictionary.
 */
export const authDocumentTitleSignUpForFigma = "auth.document_title.sign_up_for_figma" as const as I18nKey
/**
 * - content: `Did you mean {domainSuggestion}?`
 * - description: Prompts the user with a known email domain suggestion on suspicion that they misspelled their address
 */
export const authEmailDomainSuggestionPrompt = "auth.email-domain-suggestion-prompt" as const as I18nKey<{ "domainSuggestion": string | number | boolean }>
/**
 * - content: `Email text input`
 * - description: aria label for email text input for sign in/sign up
 */
export const authEmailInputAriaLabel = "auth.email-input-aria-label" as const as I18nKey
/**
 * - content: `Email`
 * - description: Placeholder text for a field into which users input their email address for authorization
 */
export const authEmailInputPlaceholder = "auth.email-input-placeholder" as const as I18nKey
/**
 * - content: `Log in to Figma to continue`
 * - description: Header on a form where users can log in to a single-sign-on-enabled account with an email address
 */
export const authEmailOnlyHeader = "auth.email-only.header" as const as I18nKey
/**
 * - content: `Log in`
 * - description: Label on a button users click to log in to a single-sign-on-enabled account with an email address
 */
export const authEmailOnlyLogIn = "auth.email-only.log-in" as const as I18nKey
/**
 * - content: `Could not find that account; did you mean to sign up?`
 * - description: Error message when the user attempts to sign in with Google SSO, but the email address they provide is not associated with any existing account
 */
export const authErrorAccountNotFound = "auth.error.account-not-found" as const as I18nKey
/**
 * - content: `We couldn't verify your request. Please try again.`
 * - description: Error message when we are unable to verify the Captcha token provided by the user in their request.
 */
export const authErrorApiCannotVerifyCaptchaToken = "auth.error.api.cannot_verify_captcha_token" as const as I18nKey
/**
 * - content: `Please enter your authenticator code`
 * - description: Error message for a user with two-factor authentication enabled who needs to enter their two-factor code to log in
 */
export const authErrorEnterTwoFactorCodeClient = "auth.error.enter-two-factor-code-client" as const as I18nKey
/**
 * - content: `Request cannot be validated.`
 * - description: An error message displayed when a login fails to be validated by the server
 */
export const authErrorMagicLinkServerValidationError = "auth.error.magic-link-server-validation-error" as const as I18nKey
/**
 * - content: `Wrong code`
 * - description: An error message during login when users enter an incorrect two-factor code and login fails.
 */
export const authErrorWrongCode = "auth.error.wrong_code" as const as I18nKey
/**
 * - content: `Create account`
 * - description: Call to action text telling a user that their account will be created when clicked
 */
export const authExtraInfoCreateAccount = "auth.extra-info.create-account" as const as I18nKey
/**
 * - content: `Please provide your job function`
 * - description: Error message when a new user has not entered what they do for work into our signup form
 */
export const authExtraInfoErrorsPleaseProvideJobFunction = "auth.extra-info.errors.please-provide-job-function" as const as I18nKey
/**
 * - content: `How will you primarily use Figma?`
 * - description: Form label for when we ask how someone will use our tool
 */
export const authExtraInfoHowWillYouUse = "auth.extra-info.how-will-you-use" as const as I18nKey
/**
 * - content: `Design`
 * - description: Job classification for a designer
 */
export const authExtraInfoJobTypeDesign = "auth.extra-info.job-type.design" as const as I18nKey
/**
 * - content: `Marketing`
 * - description: Job classification for someone working in marketing
 */
export const authExtraInfoJobTypeMarketing = "auth.extra-info.job-type.marketing" as const as I18nKey
/**
 * - content: `Other`
 * - description: Catch-all for folks whose jobs don't match any of our other descriptions
 */
export const authExtraInfoJobTypeOther = "auth.extra-info.job-type.other" as const as I18nKey
/**
 * - content: `Product management`
 * - description: Job classification for someone working in Product
 */
export const authExtraInfoJobTypeProductManagement = "auth.extra-info.job-type.product-management" as const as I18nKey
/**
 * - content: `Software development`
 * - description: Job classification for someone working in software development
 */
export const authExtraInfoJobTypeSoftwareDevelopment = "auth.extra-info.job-type.software-development" as const as I18nKey
/**
 * - content: `Subscribe to Figma tips and updates*`
 * - description: Checkbox for users to agree to joining our marketing mailing list. The '*' denotes that this is paired with `auth.extra-info.subscribe-to-figma-tips-and-updates-terms`.
 */
export const authExtraInfoSubscribeToFigmaTipsAndUpdates = "auth.extra-info.subscribe-to-figma-tips-and-updates" as const as I18nKey
/**
 * - content: `*By opting in, you are consenting to receive product, service and events updates from Figma. You can unsubscribe at any time.`
 * - description: Additional terms for users agreeing to join our marketing mailing list. The initial '*' denotes that this is paired with `auth.extra-info.subscribe-to-figma-tips-and-updates`
 */
export const authExtraInfoSubscribeToFigmaTipsAndUpdatesTerms = "auth.extra-info.subscribe-to-figma-tips-and-updates-terms" as const as I18nKey
/**
 * - content: `Tell us about yourself`
 * - description: Form header for when we're asking for additional personal information (job info, name, etc)
 */
export const authExtraInfoTellUsAboutYourself = "auth.extra-info.tell-us-about-yourself" as const as I18nKey
/**
 * - content: `For teaching or taking a class`
 * - description: When asked how they use our software, they refer to using it in an educational setting
 */
export const authExtraInfoUsagePurposeEdu = "auth.extra-info.usage-purpose.edu" as const as I18nKey
/**
 * - content: `For personal use`
 * - description: When asked how they use our software, they refer to using it for personal projects
 */
export const authExtraInfoUsagePurposePersonal = "auth.extra-info.usage-purpose.personal" as const as I18nKey
/**
 * - content: `For work`
 * - description: When asked how they use our software, they refer to using it for their regular job
 */
export const authExtraInfoUsagePurposeWork = "auth.extra-info.usage-purpose.work" as const as I18nKey
/**
 * - content: `What kind of work do you do?`
 * - description: Form label for when we ask what a new user does for work
 */
export const authExtraInfoWhatKindOfWork = "auth.extra-info.what-kind-of-work" as const as I18nKey
/**
 * - content: `What kind of work do you do? (required)`
 * - description: Form label for when we ask what a new user does for work, marked to note that this is a required field (for screen-reader technology)
 */
export const authExtraInfoWhatKindOfWorkRequired = "auth.extra-info.what-kind-of-work-required" as const as I18nKey
/**
 * - content: `Your name`
 * - description: Placeholder text for a form input to let users enter their names
 */
export const authExtraInfoYourName = "auth.extra-info.your-name" as const as I18nKey
/**
 * - content: `Figma logo that can be used to navigate to homepage`
 * - description: In the auth flow, we have a Figma logo that can be clicked so that the user can navigate to the homepage.
 */
export const authFigmaLogoLabel = "auth.figma_logo.label" as const as I18nKey
/**
 * - content: `Reset password`
 * - description: Link to a form where the user can reset their password if they have forgotten it
 */
export const authFooterForgotPassword = "auth.footer-forgot-password" as const as I18nKey
/**
 * - content: `Use single sign-on`
 * - description: Link prompting the user to log in or sign up with a SAML SSO credential
 */
export const authFooterSamlLink = "auth.footer-saml-link" as const as I18nKey
/**
 * - content: `Create one`
 * - description: Link from the sign-in to the sign-up form where the user can create a new account
 */
export const authFooterSignInSwitchFormLink = "auth.footer-sign-in-switch-form-link" as const as I18nKey
/**
 * - content: `No account?`
 * - description: Asks the user if they do not yet have an account so that they can create one by signing up
 */
export const authFooterSignInSwitchFormText = "auth.footer-sign-in-switch-form-text" as const as I18nKey
/**
 * - content: `Log in`
 * - description: Link from the sign-up to the sign-in form where the user can log in using their existing account
 */
export const authFooterSignUpSwitchFormLink = "auth.footer-sign-up-switch-form-link" as const as I18nKey
/**
 * - content: `Already have an account?`
 * - description: Asks the user if they have an existing account so that they can sign in instead of creating another
 */
export const authFooterSignUpSwitchFormText = "auth.footer-sign-up-switch-form-text" as const as I18nKey
/**
 * - content: `An error has occurred. Please try again.`
 * - description: Generic error message flashed to a user when there's an authentication error
 */
export const authGenericAppAuthError = "auth.generic-app-auth-error" as const as I18nKey
/**
 * - content: `Go back and try again`
 * - description: Instructing users to go back to a previous step and try it again
 */
export const authGenericGoBackAndTryAgain = "auth.generic-go-back-and-try-again" as const as I18nKey
/**
 * - content: `Loading…`
 * - description: Generic loading text to display while an operation is pending
 */
export const authGenericLoading = "auth.generic-loading" as const as I18nKey
/**
 * - content: `or`
 * - description: Text instructing a user that they can select either one option or another
 */
export const authGenericOr = "auth.generic-or" as const as I18nKey
/**
 * - content: `Go to the browser to complete login`
 * - description: Telling desktop users to open their browser to complete the login process
 */
export const authGoToBrowserToCompleteLogin = "auth.go-to-browser-to-complete-login" as const as I18nKey
/**
 * - content: `Redirecting to your organization’s SSO provider...`
 * - description: Toast message shown when a user attempted to login with a non-SSO method, and we are redirecting them to their organization's SSO identity provider instead due to the organization's SSO requirements
 */
export const authGoogleSsoRedirectingToSsoProvider = "auth.google-sso.redirecting-to-sso-provider" as const as I18nKey
/**
 * - content: `Unable to authenticate`
 * - description: Generic error message telling a user that their attempted login using Google SSO failed for unspecified reasons
 */
export const authGoogleSsoUnableToAuth = "auth.google-sso.unable-to-auth" as const as I18nKey
/**
 * - content: `Unable to get profile information from Google`
 * - description: Error message displayed when our app tries to use Google SSO to authenticate a user but fails to retrieve their information for some reason
 */
export const authGoogleSsoUnableToGetInfo = "auth.google-sso.unable-to-get-info" as const as I18nKey
/**
 * - content: `Your email cannot be the same as your password.`
 * - description: Error message displayed when a user attempts to create a password that is the same as their email address
 */
export const authInputValidationEmailIsPassword = "auth.input-validation.email-is-password" as const as I18nKey
/**
 * - content: `Hmm, that email address doesn't look right.`
 * - description: Error message displayed when a user submits a signup form with an invalid or malformed email address
 */
export const authInputValidationInvalidEmail = "auth.input-validation.invalid-email" as const as I18nKey
/**
 * - content: `OK, that password is hilariously long!`
 * - description: Error message displayed when a user attempts to create a password that is too long
 */
export const authInputValidationLongPassword = "auth.input-validation.long-password" as const as I18nKey
/**
 * - content: `Your name cannot be the same as your password.`
 * - description: Error message displayed when a user attempts to create a password that is the same as their name
 */
export const authInputValidationNameIsPassword = "auth.input-validation.name-is-password" as const as I18nKey
/**
 * - content: `Please provide your email.`
 * - description: Error message displayed when a user submits a signup form without entering a (required) email address
 */
export const authInputValidationNoEmail = "auth.input-validation.no-email" as const as I18nKey
/**
 * - content: `Please provide your password.`
 * - description: Error message displayed when a user submits a signup form without entering a (required) password
 */
export const authInputValidationNoPassword = "auth.input-validation.no-password" as const as I18nKey
/**
 * - content: `Please set a password longer than seven characters.`
 * - description: Error message displayed when a user attempts to create a password that is too short (7 characters or fewer)
 */
export const authInputValidationShortPassword = "auth.input-validation.short-password" as const as I18nKey
/**
 * - content: `Log in as {email}`
 * - description: Header text inviting the user to begin an app session using the currrently authorized / logged-in account
 */
export const authLogInWithAccount = "auth.log-in-with-account" as const as I18nKey<{ "email": string | number | boolean }>
/**
 * - content: `Log in with browser`
 * - description: If users are in the desktop/mobile apps, this will take the user to a web browser to log into Figma
 */
export const authLogInWithBrowser = "auth.log-in-with-browser" as const as I18nKey
/**
 * - content: `Not {email}? {logInLink}`
 * - description: Text asking the user if they are not the currently authorized user, and inviting them to log in with a different account if so
 */
export const authLogInWithOtherAccount = "auth.log-in-with-other-account" as const as I18nKey<{ "email": string | number | boolean; "logInLink": string | number | boolean }>
/**
 * - content: `Log in with another account`
 * - description: Clickable link text inviting the user to log in with a different account than the currently authorized one
 */
export const authLogInWithOtherAccountLink = "auth.log-in-with-other-account-link" as const as I18nKey
/**
 * - content: `Log in to collaborate on {displayName}`
 * - description: This string appears in the header of the login modal on logged-out Community pages
 */
export const authLogInToCollaborateOnDisplayName = "auth.log_in_to_collaborate_on_display_name" as const as I18nKey<{ "displayName": string | number | boolean }>
/**
 * - content: `Log in to collaborate on this file`
 * - description: This string appears in the header of the login modal on logged-out Community pages
 */
export const authLogInToCollaborateOnThisFile = "auth.log_in_to_collaborate_on_this_file" as const as I18nKey
/**
 * - content: `Click on the link we sent to {emailAddress} within the next 5 minutes to log in.`
 * - description: Body text in screen that appears after you request a magic link to log in.
 */
export const authMagicLinkCheckEmailBodyLoginV2 = "auth.magic_link_check_email.body_login_v2" as const as I18nKey<{ "emailAddress": string | number | boolean }>
/**
 * - content: `Or, enter the code we sent to your email here.`
 * - description: Body text in screen that appears after you request a magic link and code to log in.
 */
export const authMagicLinkCheckEmailBodyLoginWithCode = "auth.magic_link_check_email.body_login_with_code" as const as I18nKey
/**
 * - content: `Click on the link we sent to {emailAddress} within the next 5 minutes to finish your account setup.`
 * - description: Body text in screen that appears after you request a magic link to sign up.
 */
export const authMagicLinkCheckEmailBodySignupV2 = "auth.magic_link_check_email.body_signup_v2" as const as I18nKey<{ "emailAddress": string | number | boolean }>
/**
 * - content: `Check your inbox`
 * - description: Heading on the screen that appears after you request a magic link to sign in or sign up
 */
export const authMagicLinkCheckEmailCheckYourInbox = "auth.magic_link_check_email.check_your_inbox" as const as I18nKey
/**
 * - content: `Go back`
 * - description: Appears as the text of a link that takes the user back to the previous state to retry within auth.magic_link_check_email.wrong_address_go_back
 */
export const authMagicLinkCheckEmailGoBack = "auth.magic_link_check_email.go_back" as const as I18nKey
/**
 * - content: `Open Gmail`
 * - description: Button after requesting a magic link, prompting users to open Gmail to redeem the magic link
 */
export const authMagicLinkCheckEmailOpenGmail = "auth.magic_link_check_email.open_gmail" as const as I18nKey
/**
 * - content: `Open Outlook`
 * - description: Button after requesting a magic link, prompting users to open Outlook to redeem the magic link
 */
export const authMagicLinkCheckEmailOpenOutlook = "auth.magic_link_check_email.open_outlook" as const as I18nKey
/**
 * - content: `Wrong address? {goBackLink}`
 * - description: Appears at the bottom of the screen users see after they request a magic link to log in or sign up. goBackLink is a link with the text auth.magic_link_check_email.go_back
 */
export const authMagicLinkCheckEmailWrongAddressGoBack = "auth.magic_link_check_email.wrong_address_go_back" as const as I18nKey<{ "goBackLink": string | number | boolean }>
/**
 * - content: `{ indicator }By opting in, you are consenting to receive product, service and events updates from Figma. You can unsubscribe at any time.`
 * - description: Additional terms for users agreeing to join our marketing mailing list. The optional indicator (e.g. a *) indicates pairing with `auth.extra-info.subscribe-to-figma-tips-and-updates`
 */
export const authMarketingSubscribeToFigmaTipsAndUpdatesTermsWithIndicator = "auth.marketing.subscribe-to-figma-tips-and-updates-terms-with-indicator" as const as I18nKey<{ "indicator": string | number | boolean }>
/**
 * - content: `Subscribe to Figma tips and updates{ indicator }`
 * - description: Checkbox for users to agree to joining our marketing mailing list. The optional indicator (e.g. a *) indicates pairing with `auth.extra-info.subscribe-to-figma-tips-and-updates-terms`.
 */
export const authMarketingSubscribeToFigmaTipsAndUpdatesWithIndicator = "auth.marketing.subscribe-to-figma-tips-and-updates-with-indicator" as const as I18nKey<{ "indicator": string | number | boolean }>
/**
 * - content: `Microsoft Teams`
 * - description: Designator for Microsoft Teams
 */
export const authMicrosoftTeams = "auth.microsoft-teams" as const as I18nKey
/**
 * - content: `mirror`
 * - description: Designator for our phone apps used to mirror prototype files
 */
export const authMirrorApp = "auth.mirror-app" as const as I18nKey
/**
 * - content: `mobile`
 * - description: Designator for our mobile phone apps
 */
export const authMobileApp = "auth.mobile-app" as const as I18nKey
/**
 * - content: `Something went wrong when attempting to redirect to the mobile app. Please try again.`
 * - description: Error shown on the mobile app authentication redeem page after an earlier attempt to hand off to the native Figma app did not work. Shown below a "Try again" button that retries via the figma:// deep link.
 */
export const authMobileRedeemHandoffFailed = "auth.mobile-redeem-handoff-failed" as const as I18nKey
/**
 * - content: `Try again`
 * - description: Button shown on the error screen of the mobile app authentication redeem page, after an earlier attempt to hand off to the native Figma app did not work. Tapping it retries opening the app via the figma:// deep link.
 */
export const authMobileRedeemTryAgain = "auth.mobile-redeem-try-again" as const as I18nKey
/**
 * - content: `No account?`
 * - description: Users might not have created their accounts with our product yet
 */
export const authNoAccount = "auth.no-account" as const as I18nKey
/**
 * - content: `No, thanks`
 * - description: This is a decline button in a newsletter subscription modal. Users click this to opt out of receiving tips and updates from Figma. It appears alongside a "Yes, please" button, giving users a clear choice about declining to receive product, service, and events communications.
 */
export const authNoThanks = "auth.no_thanks" as const as I18nKey
/**
 * - content: `Not seeing the browser tab?`
 * - description: Guidance for users if they haven't automatically seen the authentication tab appear in their browsers
 */
export const authNotSeeingTheBrowserTab = "auth.not-seeing-the-browser-tab" as const as I18nKey
/**
 * - content: `Create a free account to continue diagramming`
 * - description: This message appears in a modal dialog when a user has been using a diagramming feature in FigJam but needs to create an account to continue. It's displayed prominently in the center of a auth modal overlay that appears on top of the user's diagram
 */
export const authOaifjCreateAFreeAccountTo = "auth.oaifj.create_a_free_account_to" as const as I18nKey
/**
 * - content: `Open Figma for VS Code`
 * - description: Instructs the user to open Figma for VS Code to complete the authentication process
 */
export const authOpenFigmaForVsCode = "auth.open-figma-for-vs-code" as const as I18nKey
/**
 * - content: `Open the {appDescriptor} app`
 * - description: Instructs the user to open one of three apps to complete the authentication process: mobile, desktop, or mirror
 */
export const authOpenSpecificApp = "auth.open-specific-app" as const as I18nKey<{ "appDescriptor": string | number | boolean }>
/**
 * - content: `Password`
 * - description: Placeholder text for a field into which users input their password for authorization
 */
export const authPasswordInputPlaceholder = "auth.password-input-placeholder" as const as I18nKey
/**
 * - content: `Back to Log in`
 * - description: Take a user back to the log in page
 */
export const authPasswordResetBackToLogIn = "auth.password-reset.back-to-log-in" as const as I18nKey
/**
 * - content: `Cancel`
 * - description: Button to exit out of the password reset workflow
 */
export const authPasswordResetCancel = "auth.password-reset.cancel" as const as I18nKey
/**
 * - content: `Choose a new password`
 * - description: Header for page where a user enters in a new password
 */
export const authPasswordResetChooseANewPassword = "auth.password-reset.choose-a-new-password" as const as I18nKey
/**
 * - content: `If an account exists for {email}, you will get an email with instructions on resetting your password. If it doesn't arrive, be sure to check your spam folder.`
 * - description: After a user requests to reset their password, these instructions appear
 */
export const authPasswordResetConfirmationText = "auth.password-reset.confirmation-text" as const as I18nKey<{ "email": string | number | boolean }>
/**
 * - content: `Enter your email to reset password`
 * - description: Heading that appears on password reset page
 */
export const authPasswordResetEnterEmailHeader = "auth.password-reset.enter-email-header" as const as I18nKey
/**
 * - content: `Confirm password`
 * - description: Placeholder text for a password confirmation input area
 */
export const authPasswordResetPlaceholderConfirmPassword = "auth.password-reset.placeholder.confirm-password" as const as I18nKey
/**
 * - content: `Password`
 * - description: Placeholder text for a password input area
 */
export const authPasswordResetPlaceholderPassword = "auth.password-reset.placeholder.password" as const as I18nKey
/**
 * - content: `Reset password`
 * - description: Confirmation button for user to submit email for a password reset
 */
export const authPasswordResetResetPasswordButton = "auth.password-reset.reset-password-button" as const as I18nKey
/**
 * - content: `Submit`
 * - description: Call-to-action that confirms that a user is ready to save their new password
 */
export const authPasswordResetSubmit = "auth.password-reset.submit" as const as I18nKey
/**
 * - content: `Log in or create an account to leave comments on {fileName}.`
 * - description: Header on a form where users log in or sign up to leave a prototype comment
 */
export const authPrototypeCommentSignedOut = "auth.prototype_comment_signed_out" as const as I18nKey<{ "fileName": string | number | boolean }>
/**
 * - content: `These passwords don't match. Try again?`
 * - description: Error message displayed during reset password attempt if user inputs the password incorrectly on retype
 */
export const authResetPasswordPasswordRetypeError = "auth.reset-password.password-retype-error" as const as I18nKey
/**
 * - content: `Returning to mobile app`
 * - description: Header text shown to user before being redirected to the mobile app.
 */
export const authReturningToMobileApp = "auth.returning-to-mobile-app" as const as I18nKey
/**
 * - content: `Log in to Figma with SAML SSO`
 * - description: Header for page where users can start logging in with a single-sign-on provider managing their account
 */
export const authSamlStartHeader = "auth.saml-start.header" as const as I18nKey
/**
 * - content: `Log in with Google or a password`
 * - description: Call to action for users that want to exit the single-sign-on workflow and use Google/their password to log in
 */
export const authSamlStartLogInGoogleOrPassword = "auth.saml-start.log-in-google-or-password" as const as I18nKey
/**
 * - content: `Log in`
 * - description: Call to action or users to log in with single-sign-on using their entered email
 */
export const authSamlStartLogInPlain = "auth.saml-start.log-in-plain" as const as I18nKey
/**
 * - content: `Continue with Google`
 * - description: Label on a button that users can click to sign in to Figma using their Google account
 */
export const authSignInGoogleText = "auth.sign-in-google-text" as const as I18nKey
/**
 * - content: `Log in`
 * - description: Label on a button that users can click to sign in to Figma with their email and password
 */
export const authSignInSubmitText = "auth.sign-in-submit-text" as const as I18nKey
/**
 * - content: `Authenticated as {email}`
 * - description: A message displayed if a user has already had a session and attempts to log in or sign up again
 */
export const authSignInSuccess = "auth.sign-in-success" as const as I18nKey<{ "email": string | number | boolean }>
/**
 * - content: `Privacy Policy`
 * - description: Text for link to Privacy Policy document
 */
export const authSignInPrivacyPolicy = "auth.sign-in.privacy-policy" as const as I18nKey
/**
 * - content: `Figma TOS`
 * - description: Text short for 'Terms of Service'. Links to Google's Terms of Service
 */
export const authSignInTosLong = "auth.sign-in.tos-long" as const as I18nKey
/**
 * - content: `Continue with email`
 * - description: Label on a button that users can click to continue to the next signup step after entering their email address
 */
export const authSignUpEmailContinueText = "auth.sign-up-email-continue-text" as const as I18nKey
/**
 * - content: `Continue with Google`
 * - description: Label on a button that users can click to sign up for Figma using their Google account
 */
export const authSignUpGoogleText = "auth.sign-up-google-text" as const as I18nKey
/**
 * - content: `Create account`
 * - description: Label on a button that users can click to submit their email and password to create a new Figma account
 */
export const authSignUpSubmitText = "auth.sign-up-submit-text" as const as I18nKey
/**
 * - content: `You may receive product, service, and events email updates from Figma and can opt-out at any time.`
 * - description: When the visitor is automatically opted into marketing emails, we provide more additional terms
 */
export const authSignUpAutoMarketingEmailOptInAdditionalTerms = "auth.sign-up.auto-marketing-email-opt-in-additional-terms" as const as I18nKey
/**
 * - content: `An error occurred while sending the confirmation email.`
 * - description: Error message displayed if the validation email failed to send
 */
export const authSignUpConfirmationEmailError = "auth.sign-up.confirmation-email-error" as const as I18nKey
/**
 * - content: `Sign in to Figma`
 * - description: Header text that appears on dedicated signin pages above a form that collects a user's email address and desired password
 */
export const authSignInToFigma = "auth.sign_in_to_figma" as const as I18nKey
/**
 * - content: `Sign up`
 * - description: This string appears on a button or link that when clicked, takes the user to sign up for Figma.
 */
export const authSignUp = "auth.sign_up" as const as I18nKey
/**
 * - content: `Sign up for Figma`
 * - description: Header text that appears on dedicated signup pages above a form that collects a user's email address and desired password
 */
export const authSignUpForFigma = "auth.sign_up_for_figma" as const as I18nKey
/**
 * - content: `Sign up to comment`
 * - description: Prompt for users to sign in to leave comments on designs
 */
export const authSignUpToComment = "auth.sign_up_to_comment" as const as I18nKey
/**
 * - content: `Sign up to comment, edit, inspect and more.`
 * - description: Header text that appears on logged out file pages
 */
export const authSignUpToCommentEditInspect = "auth.sign_up_to_comment_edit_inspect" as const as I18nKey
/**
 * - content: `Sign up to comment, edit, preview, and more.`
 * - description: Header text that appears on logged out sites file pages
 */
export const authSignUpToCommentEditPreview = "auth.sign_up_to_comment_edit_preview" as const as I18nKey
/**
 * - content: `Sign up to edit`
 * - description: Prompt for users to sign in to edit content
 */
export const authSignUpToEdit = "auth.sign_up_to_edit" as const as I18nKey
/**
 * - content: `Sign up to edit, comment, and more.`
 * - description: Toolbelt banner text that appears in logged-out FigJam files
 */
export const authSignUpToEditComment = "auth.sign_up_to_edit_comment" as const as I18nKey
/**
 * - content: `Sign up to edit your diagram, comment, and more.`
 * - description: Toolbelt banner text that appears in logged-out FigJam files that have been pre-populated with a diagram
 */
export const authSignUpToEditDiagram = "auth.sign_up_to_edit_diagram" as const as I18nKey
/**
 * - content: `Sign up to use Figma Make`
 * - description: This text appears in a banner within the sidebar of a logged-out Figma file. It encourages users to sign up for a feature called "Figma Make". The banner includes a sign-up button and a Google logo, suggesting multiple sign-up options. The text serves as a call-to-action, prompting users to create an account to access additional functionality.
 */
export const authSignUpToUseFigmaRev = "auth.sign_up_to_use_figma_rev" as const as I18nKey
/**
 * - content: `Sign up to use Figma Weave`
 * - description: This text appears in a banner at the bottom of a logged-out Figma Weave file. It encourages users to sign up for a feature called "Figma Weave". The banner includes a sign-up button and a Google logo, suggesting multiple sign-up options. The text serves as a call-to-action, prompting users to create an account to access additional functionality.
 */
export const authSignUpToUseFigmaWeave = "auth.sign_up_to_use_figma_weave" as const as I18nKey
/**
 * - content: `Sign up to use inspection tools`
 * - description: Prompt for users to sign in to access design inspection tools
 */
export const authSignUpToUseInspectionTools = "auth.sign_up_to_use_inspection_tools" as const as I18nKey
/**
 * - content: `Sign up to use multiplayer tools`
 * - description: Prompt for users to sign in to access multiplayer collaboration tools
 */
export const authSignUpToUseMultiplayerTools = "auth.sign_up_to_use_multiplayer_tools" as const as I18nKey
/**
 * - content: `You're currently signed into the accounts below, but none belong to the organization that published this app. To continue, please log in with the correct work account.`
 * - description: Description for the app sso page
 */
export const authSitesSsoAppSsoDescription = "auth.sites-sso.app-sso-description" as const as I18nKey
/**
 * - content: `You do not have access to the published app`
 * - description: Header for the sites sso page
 */
export const authSitesSsoAppSsoHeader = "auth.sites-sso.app-sso-header" as const as I18nKey
/**
 * - content: `Log in to another account`
 * - description: Button for the sites sso page
 */
export const authSitesSsoSitesSsoButton = "auth.sites-sso.sites-sso-button" as const as I18nKey
/**
 * - content: `You're currently signed into the accounts below, but none belong to the organization that published this site. To continue, please log in with the correct work account.`
 * - description: Description for the sites sso page
 */
export const authSitesSsoSitesSsoDescription = "auth.sites-sso.sites-sso-description" as const as I18nKey
/**
 * - content: `You do not have access to the published site`
 * - description: Header for the sites sso page
 */
export const authSitesSsoSitesSsoHeader = "auth.sites-sso.sites-sso-header" as const as I18nKey
/**
 * - content: `Cancel and do not join {orgName}`
 * - description: Link to abandon an organization's sign-in flow, avoid joining the organization and go back to a user's existing session
 */
export const authSsoGateBackWithSession = "auth.sso-gate.back-with-session" as const as I18nKey<{ "orgName": string | number | boolean }>
/**
 * - content: `Go back to change email`
 * - description: Link to abandon an organization's sign-in flow when the user does not have a current / existing session
 */
export const authSsoGateBackWithoutSession = "auth.sso-gate.back-without-session" as const as I18nKey
/**
 * - content: `Google`
 * - description: Name of Google (the company) presented as a means of logging in to a single-sign-on account
 */
export const authSsoGateGoogle = "auth.sso-gate.google" as const as I18nKey
/**
 * - content: `To continue, join {orgName} by logging in with {ssoName}`
 * - description: Header of a form where users log in to a single-sign-on account associated with a specific company / organization
 */
export const authSsoGateHeader = "auth.sso-gate.header" as const as I18nKey<{ "orgName": string | number | boolean; "ssoName": string | number | boolean }>
/**
 * - content: `Session expired or invalid`
 * - description: Error message for users who attempt to accept an invite to an org resource with an invalid session
 */
export const authSsoGateInvalidSession = "auth.sso-gate.invalid-session" as const as I18nKey
/**
 * - content: `Continue with Google`
 * - description: Label on a button to sign in to an organization-associated account using Google as an identity provider
 */
export const authSsoGateLogInGoogle = "auth.sso-gate.log-in-google" as const as I18nKey
/**
 * - content: `Log in with SAML`
 * - description: Label on a button to sign in to an organization-associated account using SAML as an identity provider
 */
export const authSsoGateLogInSaml = "auth.sso-gate.log-in-saml" as const as I18nKey
/**
 * - content: `SAML SSO`
 * - description: Abbreviated name for a type of authorization (SAML) presented as a means of logging in to a single-sign-on account
 */
export const authSsoGateSamlSso = "auth.sso-gate.saml-sso" as const as I18nKey
/**
 * - content: `Back to email entry`
 * - description: Accessible label on a back-arrow icon button that returns the user to the email entry form from the IdP picker
 */
export const authSsoSelectionBack = "auth.sso-selection.back" as const as I18nKey
/**
 * - content: `Select any organization to authenticate and access all of {email}’s organizations.`
 * - description: Subheader on the IdP picker explaining that each button represents one of the user's organizations. {email} is the email the user entered on the previous step and is rendered inline in a slightly heavier weight.
 */
export const authSsoSelectionDescription = "auth.sso-selection.description" as const as I18nKey<{ "email": string | number | boolean }>
/**
 * - content: `Continue to Figma`
 * - description: Header of the IdP picker shown when a user can sign in via multiple organizations' SSO
 */
export const authSsoSelectionHeader = "auth.sso-selection.header" as const as I18nKey
/**
 * - content: `Previously logged in with`
 * - description: Header above the most-recently-used SSO option in the IdP picker. Appears only when the user has previously signed in on this device.
 */
export const authSsoSelectionMruLabel = "auth.sso-selection.mru-label" as const as I18nKey
/**
 * - content: `Unverified by your admin`
 * - description: Header above SSO options whose org domain has NOT been verified by an admin. Shown only when the verified-first picker layout is active.
 */
export const authSsoSelectionUnverifiedGroupLabel = "auth.sso-selection.unverified-group-label" as const as I18nKey
/**
 * - content: `Verified by your admin`
 * - description: Header above SSO options whose org domain has been verified by an admin. Shown only when the verified-first picker layout is active.
 */
export const authSsoSelectionVerifiedGroupLabel = "auth.sso-selection.verified-group-label" as const as I18nKey
/**
 * - content: `View all`
 * - description: Link that expands the IdP picker to show unverified options below the verified ones.
 */
export const authSsoSelectionViewAllLink = "auth.sso-selection.view-all-link" as const as I18nKey
/**
 * - content: `Don’t see your authentication option?`
 * - description: Lead-in text above the View all link in the IdP picker.
 */
export const authSsoSelectionViewAllPrompt = "auth.sso-selection.view-all-prompt" as const as I18nKey
/**
 * - content: `An error occurred while redeeming the invite link`
 * - description: A message that appears when a user tries to use a link sent to them to join a team, but an unknown error occurs
 */
export const authTeamJoinLinkErrorOnRedeem = "auth.team_join_link.error_on_redeem" as const as I18nKey
/**
 * - content: `Tell us about yourself`
 * - description: This string appears in the signup modal when we ask users how they use Figma
 */
export const authTellUsAboutYourself = "auth.tell_us_about_yourself" as const as I18nKey
/**
 * - content: `By clicking "{googleText}" or "{callToActionText}", you agree to the {termsOfService} and acknowledge the {privacyPolicy}.`
 * - description: Helper text to remind users that they are agreeing to specific terms and policies when completing the signup process. Displayed in the second person, with the subject being the viewer of the website. callToActionText refers to a button that displays 'Create Account' (auth.sign-up-submit-text) and googleText refers to a button that says 'Sign up with Google' (id: auth.sso-gate.log-in-google)
 */
export const authTermsAndConditions2ndPersonGoogleFirst = "auth.terms-and-conditions-2nd-person-google-first" as const as I18nKey<{ "googleText": string | number | boolean; "callToActionText": string | number | boolean; "termsOfService": string | number | boolean; "privacyPolicy": string | number | boolean }>
/**
 * - content: `By clicking "{googleText}", you agree to the {termsOfService} and acknowledge the {privacyPolicy}.`
 * - description: Helper text to remind users that they are agreeing to specific terms and policies when completing the signup process. Displayed in the second person, with the subject being the viewer of the website. googleText refers to a button that says 'Sign up with Google' (id: auth.sso-gate.log-in-google)
 */
export const authTermsAndConditions2ndPersonGoogleOnly = "auth.terms-and-conditions-2nd-person-google-only" as const as I18nKey<{ "googleText": string | number | boolean; "termsOfService": string | number | boolean; "privacyPolicy": string | number | boolean }>
/**
 * - content: `Try again in Figma for VS Code`
 * - description: Instructs user to try again in Figma for VS Code
 */
export const authTryAgainInFigmaForVsCode = "auth.try-again-in-figma-for-vs-code" as const as I18nKey
/**
 * - content: `Try again on the {appDescriptor} app`
 * - description: Instructs user to try authenticating again in one of three apps: mobile, desktop, or mirror
 */
export const authTryAgainOnSpecificApp = "auth.try-again-on-specific-app" as const as I18nKey<{ "appDescriptor": string | number | boolean }>
/**
 * - content: `Try again`
 * - description: Text on a button that refreshes the page when clicked. Shown on an auth page that tells the user they need to enable browser cookies to be able to log in and use Figma
 */
export const authTryAgain = "auth.try_again" as const as I18nKey
/**
 * - content: `Authentication code`
 * - description: Placeholder text for a field for users to enter their two-factor authentication code
 */
export const authTwoFactorAuthenticationCode = "auth.two-factor.authentication-code" as const as I18nKey
/**
 * - content: `Back`
 * - description: Button text for users that want to go to a previous step in the auth flow
 */
export const authTwoFactorBack = "auth.two-factor.back" as const as I18nKey
/**
 * - content: `Code was sent to number ending in {phoneNumber}`
 * - description: Helper text after user enters phone number to send 2-factor code to. String constructed entirely on the client
 */
export const authTwoFactorCodeHintConfirmation = "auth.two-factor.code-hint-confirmation" as const as I18nKey<{ "phoneNumber": string | number | boolean }>
/**
 * - content: `Please enter your authentication code or recovery code`
 * - description: Header for a form where users are entering their two-factor authentication code
 */
export const authTwoFactorHeader = "auth.two-factor.header" as const as I18nKey
/**
 * - content: `Log in`
 * - description: Button text for users who have finished entering their two-factor authentication code and want to log in
 */
export const authTwoFactorLogIn = "auth.two-factor.log-in" as const as I18nKey
/**
 * - content: `Resend SMS code`
 * - description: Call-to-action text for users who need their two-factor code re-sent to their phones
 */
export const authTwoFactorResendSmsCode = "auth.two-factor.resend-sms-code" as const as I18nKey
/**
 * - content: `Send SMS code`
 * - description: Call-to-action text for users who need their two-factor code sent to their phones for the first time
 */
export const authTwoFactorSendSmsCode = "auth.two-factor.send-sms-code" as const as I18nKey
/**
 * - content: `SMS sent to number ending in {phoneNumber}`
 * - description: Helper text hint guiding users to where their 2-factor code has been sent. String constructed entirely on the client
 */
export const authTwoFactorSmsHint = "auth.two-factor.sms-hint" as const as I18nKey<{ "phoneNumber": string | number | boolean }>
/**
 * - content: `An error occurred resending the code`
 * - description: Error message displayed if code failed to resend
 */
export const authValidateCodeCodeResendError = "auth.validate-code.code-resend-error" as const as I18nKey
/**
 * - content: `The code has been resent. Please check your email.`
 * - description: Confirmation message displayed once code has been sent to user email
 */
export const authValidateCodeCodeResentCheckEmail = "auth.validate-code.code-resent-check-email" as const as I18nKey
/**
 * - content: `Please enter a code`
 * - description: Message displayed if no code is entered when attempted to validate
 */
export const authValidateCodeEnterCodeMessage = "auth.validate-code.enter-code-message" as const as I18nKey
/**
 * - content: `Verify your account`
 * - description: Header for a page where a user can verify their account by providing us a code that has been sent to their email address
 */
export const authValidateCodeHeader = "auth.validate-code.header" as const as I18nKey
/**
 * - content: `A code has been sent to your email {email}.`
 * - description: Instructs a user that the account verification code has been sent to their email address
 */
export const authValidateCodePrimaryDescription = "auth.validate-code.primary-description" as const as I18nKey<{ "email": string | number | boolean }>
/**
 * - content: `Resend`
 * - description: Button that sends an account verification code to a user again after a first attempt failed (or was delayed for too long)
 */
export const authValidateCodeResendButton = "auth.validate-code.resend-button" as const as I18nKey
/**
 * - content: `Didn't receive the code? {cta}`
 * - description: Helper text for users that did not receive the code sent to them. The call-to-action is a button that lets them resend the code
 */
export const authValidateCodeResendHelperText = "auth.validate-code.resend-helper-text" as const as I18nKey<{ "cta": string | number | boolean }>
/**
 * - content: `Enter the code below to verify.`
 * - description: Instructions for where a user should enter the account verification code sent to them
 */
export const authValidateCodeSecondaryDescription = "auth.validate-code.secondary-description" as const as I18nKey
/**
 * - content: `Submit`
 * - description: Button to submit an account verification code send to a user
 */
export const authValidateCodeSubmitButton = "auth.validate-code.submit-button" as const as I18nKey
/**
 * - content: `Click on the link we sent to {emailAddress} to finish your account setup.`
 * - description: Sign-up success confirmation message that tells the user to check their email for an account verification link
 */
export const authValidateEmailClickTheLink = "auth.validate-email.click_the_link" as const as I18nKey<{ "emailAddress": string | number | boolean }>
/**
 * - content: `Log out`
 * - description: Clickable link text to log out of the currently authorized account
 */
export const authValidateEmailLogOutLink = "auth.validate-email.log-out-link" as const as I18nKey
/**
 * - content: `Wrong address? {logOutLink} to sign in with a different email.`
 * - description: Text instructing the user to log out
 */
export const authValidateEmailLogOutOnly = "auth.validate-email.log-out-only" as const as I18nKey<{ "logOutLink": string | number | boolean }>
/**
 * - content: `No email in your inbox or spam folder? {resendEmailLink}.`
 * - description: Text asking if the user failed to receive their account verification link by email, and inviting them to have it resent
 */
export const authValidateEmailResendEmail = "auth.validate-email.resend-email" as const as I18nKey<{ "resendEmailLink": string | number | boolean }>
/**
 * - content: `Let’s resend it`
 * - description: Clickable link text to resend the user's account verification email if it did not arrive
 */
export const authValidateEmailResendEmailLink = "auth.validate-email.resend-email-link" as const as I18nKey
/**
 * - content: `Resend email`
 * - description: Additional clickable link text to resend the user's account verification email if it did not arrive
 */
export const authValidateEmailResendEmailText = "auth.validate-email.resend-email-text" as const as I18nKey
/**
 * - content: `Welcome to Figma`
 * - description: Text welcoming a user to our product & company: Figma
 */
export const authWelcomeToFigma = "auth.welcome-to-figma" as const as I18nKey
/**
 * - content: `Yes, please`
 * - description: This is an affirmative button in a newsletter subscription modal. Users click this to opt in to receiving tips and updates from Figma. It appears alongside a "No, thanks" button, giving users a clear choice about consenting to receive product, service, and events communications.
 */
export const authYesPlease = "auth.yes_please" as const as I18nKey
/**
 * - content: `Your login session has expired.`
 * - description: Tells user that they have to try logging in again
 */
export const authYourLoginSessionHasExpired = "auth.your-login-session-has-expired" as const as I18nKey
/**
 * - content: `It looks like you're signed in to 10 accounts. You'll need to log out of one of these accounts before you can sign in to another one.`
 * - description: This text appears in the body of a modal dialog informing the user that they cannot log in to an additional account until they log out of one of their existing accounts. The number 10 is universal for all users and does not change.
 */
export const multiAccountMaxAccountsBody = "multi-account.max_accounts_body" as const as I18nKey
/**
 * - content: `Got it`
 * - description: This text appears as the only button in a modal dialog informing the user that they cannot log in to an additional account until they log out of one of their existing accounts. Clicking the button closes the dialog but performs no further action.
 */
export const multiAccountMaxAccountsSubmit = "multi-account.max_accounts_submit" as const as I18nKey
/**
 * - content: `You've reached the maximum number of accounts`
 * - description: This text appears in the title of a modal dialog informing the user that they cannot log in to an additional account until they log out of one of their existing accounts.
 */
export const multiAccountMaxAccountsTitle = "multi-account.max_accounts_title" as const as I18nKey
/**
 * - content: `Your session ended. Please log back in.`
 * - description: Main text in a modal shown when user's logged-in session was terminated, prompting the user to re-authenticate.
 */
export const sessionTerminatedModalBodySimple = "session_terminated_modal.body.simple" as const as I18nKey
/**
 * - content: `You’ve been logged out`
 * - description: Title of modal shown when user's logged-in session was terminated
 */
export const sessionTerminatedModalTitle = "session_terminated_modal.title" as const as I18nKey
